"use client";
import { useEffect, useState, useRef } from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import apiFetch from "@/lib/fetch";
import VenueCard from "@/components/views/list/card/VenueCard";
import { VenueLoading } from "@/components/views/list/venue-list/Venue";
import { useTrainerContext } from "@/context/TrainerContext";
import { SectionHeadingTitle } from "@/components/SectionTitle";
import MapIcon from "@heroicons/react/24/solid/MapIcon";
import { useTranslation } from "@/hooks/translationContext";
import { useUserLocation } from "@/context/UserLocationContext";

interface Props {
  data: any;
}

export default function VenuesOfTrainersClientSection(props: Props) {
  const [data, setData] = useState<any>(props.data);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { trainerFilters } = useTrainerContext();

  const {
    locationPermission,
    userLocation,
    venueDistances,
    calculateDistances,
  } = useUserLocation();

  const calculatedVenueIds = useRef<Set<number>>(new Set());

  // Calculate distances when user location is available
  useEffect(() => {
    if (locationPermission === "granted" && userLocation && data?.response?.length > 0) {
      const venuesToCalculate = data.response
        .filter((venue: any) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: any) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        venuesToCalculate.forEach((v: { id: number }) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, data, calculateDistances]);

  const fetchTrainers = async (page: number, newData: boolean) => {
    let url = `trainer/venues?index=${page}`;

    if (trainerFilters.districtId)
      url += `&districtId=${trainerFilters.districtId}`;
    if (trainerFilters.occupationId)
      url += `&occupationId=${trainerFilters.occupationId}`;

    apiFetch({ route: url }).then((res) => {
      setIsLoading(false);
      setHasMore(true);

      if (res.response?.length === 0) setHasMore(false);

      setData((prev: any) => {
        const response = newData
          ? prev.response.concat(res.response)
          : res.response;
        return {
          response,
          summary: prev?.summary,
        };
      });
    });
  };

  const next = (page: number) => {
    if (isLoading) return;

    setIsLoading(true);
    fetchTrainers(page, true);
  };

  useEffect(() => {
    setData(props.data);

    if (props.data?.response?.length >= props.data?.summary?.totalDocs)
      setHasMore(false);
    else setHasMore(true);
  }, [props.data]);

  useEffect(() => {
    fetchTrainers(0, false);
  }, [trainerFilters]);

  if (!isLoading && data.response?.length == 0) return <></>;

  return (
    <div className="border-t border-stone-50 py-4">
      <TitleSection />

      <InfiniteScroll
        skeletonElementId="venue-card-skeleton"
        isLoading={isLoading}
        hasMore={hasMore}
        loading={<VenueLoading count={6} />}
        next={next}
        dataLength={data.response?.length || 0}
      >
        <div className="xl:grid justify-items-center grid-cols-1 flex flex-wrap justify-center xl:grid-cols-3 gap-5">
          {Array.isArray(data.response) &&
            data.response?.map((venue: Venue.List, index: number) => {
              const distance = venueDistances.get(venue.id);
              return (
                <VenueCard titleHeadingLevel="h3" key={index} distance={distance} {...venue} />
              );
            })}
        </div>
      </InfiniteScroll>
    </div>
  );
}

function TitleSection() {
  const { t } = useTranslation()

  const title = t("text-menu-venue-filter", {
    defaultValue: "Çalışanların Çalıştığı Mekanlar",
  });

  return <SectionHeadingTitle title={title} icon={MapIcon} />;
}
